import {type ComponentProps, type FC} from 'react';

import {Alert, AlertBody, AlertHeading} from '@/modules/foundation/components/alert';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';
import {cn} from '@/modules/foundation/shared/tailwind';
import type {CommerceProductColor} from '@/sanity/schemas/types';

import {Checkbox} from '../../form/Checkbox';
import {RadioTileFrame} from '../../form/RadioTileFrame';
import {FormattedItemPrice} from '../../item/FormattedItemPrice';
import type {ItemWithVatTypeFragment} from '../../item/query';
import {ProductColorSelector} from '../../product-color/ProductColorSelector';

import type {
	ClothingPrintOptionConfiguration,
	ClothingPrintOptionGroupValidationError,
} from './configuration';
import type {
	ClothingPrintOptionGroupForSelectorFragment,
	MultiColorPrintCollectionForSelectorFragment,
	SingleColorPrintCollectionForSelectorFragment,
} from './query';

export type ClothingPrintOptionSelectorProps = {
	optionGroup: ClothingPrintOptionGroupForSelectorFragment;
	defaultConfiguration: ClothingPrintOptionConfiguration | null;
	validationError: ClothingPrintOptionGroupValidationError | null;
} & ComponentProps<'div'>;

export const ClothingPrintOptionSelector: FC<ClothingPrintOptionSelectorProps> = ({
	optionGroup,
	defaultConfiguration,
	validationError,
	className,
	...rest
}) => {
	const optionGroupId = optionGroup._key;

	return (
		<div
			className={cn(
				'flex flex-col items-stretch gap-2 rounded-sm border-2 border-neutral-600 bg-neutral-800/50 leading-0 font-semibold select-none',
				'min-h-12 px-3 text-base lg:min-h-14 lg:px-4 lg:text-lg',
				'has-[*>:checked]:border-primary has-[*>:checked]:outline-2 has-[*>:checked]:-outline-offset-4 has-[*>:checked]:outline-neutral-800',
				className,
			)}
			{...rest}
		>
			{/* We always enable required option groups through a hidden input field since the
			    disabled checkbox won't be posted with the form. */}
			{optionGroup.required && (
				<input type="hidden" defaultValue="enabled" name={`options[${optionGroupId}]`} />
			)}
			<Checkbox
				value="enabled"
				name={`options[${optionGroupId}]`}
				defaultChecked={optionGroup.required ? true : !!defaultConfiguration}
				disabled={optionGroup.required}
				labelProps={{
					className: 'flex h-12 lg:h-14',
				}}
			>
				{optionGroup.name}
			</Checkbox>
			{defaultConfiguration && (
				<div className="mt-2 flex flex-col gap-4 pb-4">
					{optionGroup.printCollections && optionGroup.printCollections.length > 0 && (
						<>
							{optionGroup.printCollections.map((collection) => {
								switch (collection._type) {
									case 'commerce.singleColorPrintCollection': {
										return (
											<SingleColorPrintSelector
												key={collection._id}
												collection={collection}
												optionGroupId={optionGroupId}
												defaultConfiguration={defaultConfiguration}
												validationError={validationError}
												item={optionGroup.singleColorPrintItem}
											/>
										);
									}
									case 'commerce.multiColorPrintCollection': {
										return (
											<MultiColorPrintSelector
												key={collection._id}
												collection={collection}
												optionGroupId={optionGroupId}
												defaultConfiguration={defaultConfiguration}
												validationError={validationError}
												item={optionGroup.multiColorPrintItem}
											/>
										);
									}
								}
							})}
							{validationError?.print && (
								<Alert severity="warning">
									<AlertHeading>Ugyldig konfigurasjon</AlertHeading>
									<AlertBody>{validationError.print}</AlertBody>
								</Alert>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
};

export type SingleColorPrintSelectorProps = {
	collection: SingleColorPrintCollectionForSelectorFragment;
	optionGroupId: string;
	defaultConfiguration: ClothingPrintOptionConfiguration | null;
	validationError: ClothingPrintOptionGroupValidationError | null;
	item: ItemWithVatTypeFragment | null;
} & ComponentProps<'div'>;

const SingleColorPrintSelector: FC<SingleColorPrintSelectorProps> = ({
	collection,
	optionGroupId,
	defaultConfiguration,
	validationError,
	item,
	...rest
}) => {
	const prints = collection.prints || [];

	const colorsById = (collection.availablePrintColors ?? []).reduce((acc, color) => {
		if (color) {
			acc.set(color._id, color);
		}

		return acc;
	}, new Map<string, CommerceProductColor>());

	for (const print of prints) {
		const color = print.preferredPrintColor;

		if (color) {
			colorsById.set(color?._id, color);
		}
	}

	const colors = colorsById ? Array.from(colorsById.values()) : [];

	return (
		<div {...rest}>
			<fieldset className="mb-3">
				<Paragraph className="mb-2 font-semibold" asChild>
					<legend>
						{collection.name}{' '}
						{item && (
							<>
								{' '}
								(+ <FormattedItemPrice items={[item]} />)
							</>
						)}
					</legend>
				</Paragraph>
				<div className="flex flex-wrap gap-2">
					{prints.map((print) => {
						const image = imageFromSanityImage(print.image);
						if (!image) {
							return null;
						}

						return (
							<RadioTileFrame
								key={print._key}
								name={`options[${optionGroupId}][printId]`}
								value={`${collection._id}:${print._key}`}
								defaultChecked={
									collection._id === defaultConfiguration?.print?.collectionId &&
									print._key === defaultConfiguration?.print?.printKey
								}
								labelProps={{
									className: 'bg-preview-background p-2',
								}}
							>
								<div
									className="group-hover:bg-secondary-dm block size-18 bg-white mask-contain mask-center mask-no-repeat"
									style={{
										maskImage: `url(${image.src})`,
									}}
								/>
							</RadioTileFrame>
						);
					})}
				</div>
			</fieldset>
			<ProductColorSelector
				legend="Trykkfarge"
				legendStyle="paragraph"
				colors={colors}
				namePrefix={`options[${optionGroupId}][printColorId]`}
				defaultConfiguration={defaultConfiguration?.printColorId ?? null}
				disableNoneSelection
				className={
					collection._id !== defaultConfiguration?.print?.collectionId
						? 'sr-only'
						: undefined
				}
			/>

			{validationError?.printColor && (
				<Alert severity="warning" className="mt-4">
					<AlertHeading>Ugyldig konfigurasjon</AlertHeading>
					<AlertBody>{validationError.printColor}</AlertBody>
				</Alert>
			)}
		</div>
	);
};

export type MultiColorPrintSelectorProps = {
	collection: MultiColorPrintCollectionForSelectorFragment;
	optionGroupId: string;
	defaultConfiguration: ClothingPrintOptionConfiguration | null;
	validationError: ClothingPrintOptionGroupValidationError | null;
	item: ItemWithVatTypeFragment | null;
} & ComponentProps<'fieldset'>;

const MultiColorPrintSelector: FC<MultiColorPrintSelectorProps> = ({
	collection,
	optionGroupId,
	defaultConfiguration,
	validationError: _,
	item,
	...rest
}) => {
	const prints = collection.prints || [];

	return (
		<fieldset {...rest}>
			<legend className="mb-2 text-lg font-semibold">
				{collection.name}{' '}
				{item && (
					<>
						{' '}
						(+ <FormattedItemPrice items={[item]} />)
					</>
				)}
			</legend>
			<div className="flex flex-wrap gap-2">
				{prints.map((print) => {
					const image = imageFromSanityImage(print.image);
					if (!image) {
						return null;
					}

					return (
						<RadioTileFrame
							key={print._key}
							value={`${collection._id}:${print._key}`}
							name={`options[${optionGroupId}][printId]`}
							defaultChecked={
								collection._id === defaultConfiguration?.print?.collectionId &&
								print._key === defaultConfiguration?.print?.printKey
							}
							labelProps={{
								className: 'bg-preview-background p-2',
							}}
						>
							<ImageBlock
								{...image}
								alt={print.name || ''}
								className="object-contain"
								style={{width: 72, height: 72}}
							/>
						</RadioTileFrame>
					);
				})}
			</div>
		</fieldset>
	);
};
